// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import type { ProcedureDetail } from '../../shared/types/procedures';
import StatusTag from './StatusTag';
import styles from './ProcedurePreview.module.css';

interface ProcedurePreviewProps {
  procedure: ProcedureDetail;
}

function PreviewSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>{label}</div>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={value ? styles.rowValue : styles.rowValueEmpty}>{value || '—'}</span>
    </div>
  );
}

function governanceLines(governance: ProcedureDetail['governance']): string[] {
  const lines: string[] = [];
  if (governance.mopRequired) lines.push('MOP required');
  if (governance.changeApproval) lines.push('Change approval');
  if (governance.loto) lines.push('LOTO');
  if (governance.redundancy) lines.push(`Redundancy: ${governance.redundancy}`);
  if (governance.escalatesToEop) lines.push(`Escalates to EOP: ${governance.escalatesToEop}`);
  return lines;
}

/**
 * Read-rendered "live preview" of a procedure — the right-hand pane on the
 * view/create screens. Pure display, no inputs; fed the same `ProcedureDetail`
 * shape as `ProcedureForm` so the two always agree.
 */
export default function ProcedurePreview({ procedure }: ProcedurePreviewProps) {
  const flags = governanceLines(procedure.governance);

  return (
    <div className={styles.preview}>
      <div className={styles.liveBar}>
        <span className={styles.liveDot} />
        <span className={styles.liveLabel}>LIVE PREVIEW</span>
      </div>
      <div className={styles.body}>
        <div className={styles.header}>
          {/* Not an <h1> — the page's own title is the real page heading; this
            is a self-contained "rendered document" preview, presentational only. */}
          <div className={styles.title}>{procedure.title || 'Untitled procedure'}</div>
          <div className={styles.subline}>
            <span className={styles.mono}>{procedure.procedureNumber}</span>
            {procedure.version && <span className={styles.mono}> / v{procedure.version}</span>}
            <StatusTag status={procedure.status} />
          </div>
          <div className={styles.chips}>
            <span className={styles.chip}>
              {procedure.classification || 'Classification not set'}
            </span>
            <span className={styles.chip}>
              {procedure.equipmentType || 'Equipment type not set'}
            </span>
          </div>
        </div>

        <div className={styles.description}>
          {procedure.description || 'No description of work yet.'}
        </div>

        <PreviewSection label="Prerequisites">
          <Row label="Qualifications" value={procedure.prerequisites.qualifications} />
          <Row label="Approvals" value={procedure.prerequisites.approvals} />
          <Row label="Precondition" value={procedure.prerequisites.precondition} />
        </PreviewSection>

        <PreviewSection label="Affected equipment">
          {procedure.affectedEquipment.length === 0 ? (
            <div className={styles.emptyNote}>No affected equipment listed.</div>
          ) : (
            procedure.affectedEquipment.map((item, index) => (
              <div key={index} className={styles.equipmentRow}>
                <span>{item.role || '—'}</span>
                <span>{item.type || '—'}</span>
                <span className={styles.mono}>{item.asset || '—'}</span>
              </div>
            ))
          )}
        </PreviewSection>

        <PreviewSection label="Procedure">
          {procedure.procedure.length === 0 ? (
            <div className={styles.emptyNote}>No steps listed.</div>
          ) : (
            procedure.procedure.map(step => (
              <div key={step.step} className={styles.stepRow}>
                <span className={styles.stepNumber}>{step.step}</span>
                <div>
                  <div>{step.action || '—'}</div>
                  {(step.check || step.gate) && (
                    <div className={styles.stepMeta}>
                      {step.check && (
                        <span className={styles.stepCheck}>{titleCase(step.check)}</span>
                      )}
                      {step.gate && <span className={styles.gateBadge}>Gate</span>}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </PreviewSection>

        <PreviewSection label="Contingencies">
          {procedure.contingencies.length === 0 ? (
            <div className={styles.emptyNote}>No contingencies listed.</div>
          ) : (
            <div className={styles.emptyNote}>{procedure.contingencies.length} listed.</div>
          )}
        </PreviewSection>

        <PreviewSection label="Closeout">
          <Row label="From" value={procedure.closeout.turnoverFrom} />
          <Row label="To" value={procedure.closeout.turnoverTo} />
          <Row label="Sign-off" value={procedure.closeout.signoffRole} />
        </PreviewSection>

        <PreviewSection label="Governance">
          {flags.length === 0 ? (
            <div className={styles.emptyNote}>No governance flags set.</div>
          ) : (
            <ul className={styles.governanceList}>
              {flags.map(flag => (
                <li key={flag}>{flag}</li>
              ))}
            </ul>
          )}
        </PreviewSection>
      </div>
    </div>
  );
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
